import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { publicListPosts } from "@/lib/blog/blog.functions";
import { Search, Rss, ChevronRight, Sparkles, Clock, Tag } from "lucide-react";

const searchSchema = z.object({
  category: z.string().optional(),
  tag: z.string().optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).optional().default(1),
});

const PER_PAGE = 12;

const listOptions = (params: { category?: string; tag?: string; q?: string; offset: number }) =>
  queryOptions({
    queryKey: ["blog", "list", params],
    queryFn: () =>
      publicListPosts({ data: { ...params, limit: PER_PAGE } as any }),
    staleTime: 60_000,
  });

export const Route = createFileRoute("/blog/")({
  validateSearch: (s) => searchSchema.parse(s),
  loaderDeps: ({ search }) => ({ ...search }),
  loader: async ({ context, deps }) => {
    const offset = ((deps.page ?? 1) - 1) * PER_PAGE;
    return context.queryClient.ensureQueryData(
      listOptions({ category: deps.category, tag: deps.tag, q: deps.q, offset }),
    );
  },
  head: ({ loaderData }) => {
    const first = loaderData?.posts?.[0];
    const title = "Blog | FIXHEN — parts for Mercedes-Benz & BMW";
    const description =
      "Installation guides, part reviews, classic Mercedes-Benz and BMW stories, and the latest news from FIXHEN.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(first?.og_url
          ? [
              { property: "og:image", content: absUrl(first.og_url) },
              { name: "twitter:image", content: absUrl(first.og_url) },
            ]
          : [
              { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
              { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
            ]),
      ],
      links: [
        { rel: "canonical", href: "https://fixhen.com/blog" },
        { rel: "alternate", type: "application/rss+xml", href: "/blog/rss.xml", title: "FIXHEN Blog" },
      ],
    };
  },
  component: BlogIndex,
});

function absUrl(path: string) {
  return path.startsWith("http") ? path : `https://fixhen.com${path}`;
}

function BlogIndex() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const offset = ((search.page ?? 1) - 1) * PER_PAGE;
  const { data } = useSuspenseQuery(
    listOptions({ category: search.category, tag: search.tag, q: search.q, offset }),
  );
  const [draftQ, setDraftQ] = useState(search.q ?? "");

  const featured = data.posts.find((p: any) => p.featured) ?? data.posts[0];
  const rest = data.posts.filter((p: any) => p.slug !== featured?.slug);
  const totalPages = Math.max(1, Math.ceil(data.total / PER_PAGE));

  return (
    <div>
      <AnnouncementBar />
      <SiteHeader />

      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-br from-slate-50 via-white to-indigo-50/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
            <Link to="/" className="hover:text-[#172554]">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 font-semibold">Blog</span>
          </div>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-ink tracking-tight">The FIXHEN Blog</h1>
              <p className="mt-2 text-slate-600 max-w-2xl">
                In-depth guides, part reviews, and stories from the garage. Written by people who love classic Mercedes-Benz and BMW.
              </p>
            </div>
            <a
              href="/blog/rss.xml"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#172554] hover:text-[#172554]"
            >
              <Rss className="h-3.5 w-3.5" /> RSS
            </a>
          </div>

          {/* Search + categories */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                navigate({ to: "/blog", search: { ...search, q: draftQ || undefined, page: 1 } as any });
              }}
              className="relative flex-1 min-w-[260px]"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
                placeholder="Search the blog…"
                className="w-full h-11 pl-10 pr-3 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#172554] focus:ring-2 focus:ring-[#172554]/20"
              />
            </form>
            <div className="flex flex-wrap items-center gap-1">
              <Link
                to="/blog"
                search={{ q: search.q } as any}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  !search.category
                    ? "bg-[#172554] border-[#172554] text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                All
              </Link>
              {data.categories.map((c: any) => {
                const active = search.category === c.slug;
                return (
                  <Link
                    key={c.slug}
                    to="/blog"
                    search={{ category: c.slug } as any}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                      active ? "text-white" : "bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    style={{
                      background: active ? c.color : undefined,
                      borderColor: active ? c.color : undefined,
                    }}
                  >
                    {c.name}
                    <span className={`ml-1 text-[10px] ${active ? "text-white/70" : "text-slate-400"}`}>{c.count}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {search.tag ? (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-600">
              Filtered by tag:
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold">
                <Tag className="h-3 w-3" /> {search.tag}
              </span>
              <Link to="/blog" search={{} as any} className="text-[#172554] hover:underline">
                × clear
              </Link>
            </div>
          ) : null}
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-10">
        {data.posts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-slate-500">No posts match the selected filters.</p>
            <Link to="/blog" search={{} as any} className="mt-3 inline-block text-sm text-[#172554] hover:underline">
              View all posts
            </Link>
          </div>
        ) : (
          <>
            {featured && (search.page ?? 1) === 1 && !search.q && !search.tag && !search.category ? (
              <Link
                to="/blog/$slug"
                params={{ slug: featured.slug }}
                className="group grid md:grid-cols-[1.4fr_1fr] gap-0 overflow-hidden rounded-3xl bg-white border border-border shadow-sm hover:shadow-xl transition"
              >
                <div className="relative aspect-[16/10] md:aspect-auto bg-slate-100">
                  {featured.cover_url ? (
                    <img
                      src={featured.cover_url}
                      alt={featured.title}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: `${featured.cover_focal_x * 100}% ${featured.cover_focal_y * 100}%` }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-6xl font-black">FIXHEN</div>
                  )}
                  {featured.category ? (
                    <span
                      className="absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow"
                      style={{ background: featured.category.color }}
                    >
                      {featured.category.name}
                    </span>
                  ) : null}
                </div>
                <div className="p-6 md:p-10 flex flex-col justify-center gap-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#172554] font-bold">Featured</div>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-ink group-hover:text-[#172554] transition">
                    {featured.title}
                  </h2>
                  {featured.excerpt ? (
                    <p className="text-slate-600 line-clamp-3">{featured.excerpt}</p>
                  ) : null}
                  <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
                    <span>{featured.author_name ?? "FIXHEN"}</span>
                    <span>·</span>
                    <span>
                      {featured.published_at &&
                        new Date(featured.published_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> {featured.reading_minutes} min</span>
                  </div>
                </div>
              </Link>
            ) : null}

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(featured && (search.page ?? 1) === 1 && !search.q && !search.tag && !search.category
                ? rest
                : data.posts
              ).map((p: any) => (
                <BlogPostCard key={p.slug} post={p} />
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-center gap-1 pt-4">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const page = i + 1;
                  const isActive = (search.page ?? 1) === page;
                  return (
                    <Link
                      key={page}
                      to="/blog"
                      search={{ ...search, page } as any}
                      className={`h-8 min-w-[32px] rounded-md text-xs font-semibold flex items-center justify-center border ${
                        isActive
                          ? "bg-[#172554] text-white border-[#172554]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {page}
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}